import { useState } from 'react'

import './education-page.css'
import CollaborationRequestForm from '../components/CollaborationRequestForm/CollaborationRequestForm'

const actionLines = [
  {
    title: 'En la educación no formal',
    description: 'Generamos contenidos, campañas y espacios de sensibilización dirigidos a la población general, para combatir el estigma y la desinformación en torno a la menstruación, el ciclo y el climaterio.'
  },
  {
    title: 'En la educación formal',
    description: 'Intervenimos en centros educativos, con el alumnado, el profesorado y las familias, incorporando un abordaje integral de la salud menstrual desde la infancia.'
  },
  {
    title: 'En la formación de profesionales',
    description: 'Impartimos charlas y talleres dirigidos a profesionales de distintos ámbitos. Además, colaboramos con universidades en la formación de personas educadoras menstruales y en contenidos relacionados con los ciclos vitales.'
  }
]

const lawSections = [
  {
    title: 'POR PRIMERA VEZ, UNA LEY ORGÁNICA MENCIONA "EDUCACIÓN MENSTRUAL"',
    articles: 'Artículo 10 ter. Medidas en el ámbito de la educación menstrual.',
    articleHighlight: 'educación menstrual',
    description: 'Las administraciones educativas, en el ámbito de sus competencias, garantizarán, en el marco de la educación afectivo-sexual, que el sistema educativo establece en los currículos de las diferentes etapas educativas, el abordaje integral de la salud durante la menstruación con perspectiva de género, interseccional y de derechos humanos en el ámbito de la educación, tanto formal como no formal, con especial atención a la eliminación de los mitos, prejuicios y estereotipos de género que generan el estigma menstrual.'
  },
  {
    title: 'EDUCACIÓN NO FORMAL',
    articles: 'Artículos 5, 5 bis, 10 ter, 10 quater, 10 quinquies y 25',
    description: 'Debe haber tanto programas públicos de educación sexual y reproductiva, lo que incluye la educación menstrual, como campañas y acciones informativas accesibles a todas. Campañas de concienciación a la población y campañas en todos los centros sanitarios. Generando y difundiendo, así, información de calidad sobre educación en materia menstrual y productos de gestión menstrual. Se harán a través de medios de comunicación social, publicidad, internet, RRSS y empresas de la tecnología de la información y comunicación, y combatirán estereotipos sobre la menstruación.'
  },
  {
    title: 'EDUCACIÓN FORMAL',
    articles: 'Artículos 5, 9, 10 y 10 ter',
    description: 'En los centros educativos de Educación Obligatoria (Primaria y Secundaria) así como en la Formación Profesional, debe haber educación menstrual o lo que es lo mismo, un abordaje integral de la salud durante la menstruación, con especial atención a la eliminación de los mitos, prejuicios y estereotipos de género que generan el estigma menstrual. También se deben realizar actividades formativas complementarias para padres, madres y tutores y para el profesorado.'
  },
  {
    title: 'FORMACIÓN DE PROFESIONALES',
    articles: 'Artículo 10 sexies',
    description: 'Quienes estudian titulaciones relacionadas con las ciencias jurídicas, las ciencias de la educación y las ciencias sociales deben recibir formación a través de la incorporación de contenidos de calidad, adaptados y suficientes sobre salud, derechos sexuales y reproductivos.'
  }
]

const EducationPage = () => {
  const [isFormOpen, setIsFormOpen] = useState(false)

  return (
    <>
    <section className='education-page'>
      <a className='education-page__back-link' href='/asociacion'>
        <span aria-hidden='true'>←</span>
        Volver
      </a>

      <header className='education-page__hero'>
        <div className='education-page__hero-copy'>
          <span className='education-page__eyebrow'>Eje de intervención</span>
          <h1>Educación menstrual</h1>
          <p>
            Como Asociación definimos nuestro trabajo en el ámbito de la educación como un enfoque pedagógico integral que busca transformar la percepción y vivencia de la menarquía, la pubertad, el ciclo ovárico y menstrual, el climaterio y la menopausia.
          </p>
        </div>

        <div className='education-page__hero-map' aria-hidden='true'>
          <span>cuerpo</span>
          <span>derechos</span>
          <span>cuidados</span>
          <span>ecosistemas</span>
        </div>
      </header>

      <div className='education-page__intro'>
        <p>
          Nuestra labor educativa promueve una reflexión crítica feminista e interseccional sobre las etapas vitales desde la perspectiva de derechos humanos, justicia social y cuidado de la vida.
        </p>
        <p>Te invitamos a leer la definición de Educación menstrual incluida en Wikipedia a partir de la colaboración de la Asociación La Vida en Rojo con el Viquiprojecte Dones i Salut.</p>
        <a href='https://es.wikipedia.org/wiki/Educaci%C3%B3n_menstrual' target='_blank' rel='noopener noreferrer' className='education-page__intro-link'>
          Ver definición
        </a>
      </div>

      <section className='education-page__commitment' aria-label='Compromiso educativo'>
        <div>
          <span className='education-page__eyebrow'>Compromiso</span>
          <p>
            Nuestro compromiso es garantizar que la <strong>educación menstrual sea reconocida como un derecho humano fundamental</strong>, asegurando que sea temprana, accesible, inclusiva y coeducativa para toda la sociedad. En el Estado español, este derecho está respaldado por la <a href="https://www.boe.es/buscar/doc.php?id=BOE-A-2023-5364" target="_blank" rel="noopener noreferrer" className='education-page__law-link'>Ley Orgánica 1/2023</a>, <strong>de 28 de febrero, por la que se modifica la Ley Orgánica 2/2010, de 3 de marzo, de salud sexual y reproductiva y de la interrupción voluntaria del embarazo.</strong>
          </p>
        </div>
      </section>

      <section className='education-page__law' aria-labelledby='education-law-title'>
        <h2 id='education-law-title'>Lo que dice la ley</h2>

        <div className='education-page__law-grid'>
          {lawSections.map((section) => {
            const renderArticlesText = () => {
              if (!section.articleHighlight) {
                return section.articles
              }
              
              const parts = section.articles.split(section.articleHighlight)
              return parts.map((part, index) => (
                <span key={index}>
                  {part}
                  {index < parts.length - 1 && (
                    <span className='education-page__law-articles-highlight'>
                      {section.articleHighlight}
                    </span>
                  )}
                </span>
              ))
            }
            
            return (
              <article className='education-page__law-item' key={section.title}>
                <h3>{section.title}</h3>
                <span className='education-page__law-articles'>{renderArticlesText()}</span>
                <p>{section.description}</p>
              </article>
            )
          })}
        </div>
      </section>

      <section className='education-page__actions' aria-labelledby='education-actions-title'>
        <div className='education-page__section-heading'>
          <span>Líneas de acción</span>
          <h2 id='education-actions-title'>Cómo llevamos la educación al territorio</h2>
        </div>

        <div className='education-page__actions-grid'>
          {actionLines.map((line, index) => (
            <article className='education-page__action' key={line.title}>
              <span className='education-page__action-number'>{String(index + 1).padStart(2, '0')}</span>
              <h3>{line.title}</h3>
              <p>{line.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className='education-page__cta' aria-labelledby='education-cta-title'>
        <div className='education-page__cta-content'>
          <h2 id='education-cta-title'>¿Hablamos?</h2>
          <p>
            Si representas a una universidad, administración pública, centro educativo, empresa, entidad social o colectivo profesional y quieres incorporar la cultura menstrual a vuestro ámbito de actuación, estaremos encantadas de diseñar una propuesta adaptada a vuestras necesidades.
          </p>
          <button onClick={() => setIsFormOpen(true)} className='education-page__cta-button'>
            Formulario de contacto
          </button>
        </div>
      </section>
    </section>

    <CollaborationRequestForm isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} />
    </>
  )
}

export default EducationPage
